import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { EditorView } from '@tiptap/pm/view'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { MentionAttrs } from '../types'

const mentionPluginKey = new PluginKey('mentionDecoration')

function getMentionAttrsAtDOM(
  view: EditorView,
  target: EventTarget | null,
): MentionAttrs | null {
  if (
    !(target instanceof HTMLElement) ||
    !target.classList.contains('mention')
  ) {
    return null
  }
  const pos = view.posAtDOM(target, 0)
  if (pos < 0) {
    return null
  }
  const node = view.state.doc.nodeAt(pos)
  if (!node || node.type.name !== 'mention') {
    return null
  }
  return node.attrs as MentionAttrs
}

function buildMentionDecorations(
  doc: ProseMirrorNode,
  isEditable: boolean,
): DecorationSet {
  if (isEditable) {
    return DecorationSet.empty
  }

  const decorations: Decoration[] = []

  doc.descendants((node, pos) => {
    if (node.type.name !== 'mention') {
      return
    }
    const char = (node.attrs.mentionSuggestionChar as string | undefined) ?? '@'
    const label = node.attrs.label ?? node.attrs.id
    decorations.push(
      Decoration.node(pos, pos + node.nodeSize, {
        tabindex: '0',
        role: 'button',
        'aria-label': `mention ${char}${label}`,
      }),
    )
  })
  return DecorationSet.create(doc, decorations)
}

export function createMentionClickPlugin(
  getOnMentionClick: () => ((attrs: MentionAttrs) => void) | undefined,
  initialIsEditable: boolean = true,
) {
  let isEditable = initialIsEditable

  return new Plugin({
    key: mentionPluginKey,
    state: {
      init(_, state) {
        return buildMentionDecorations(state.doc, isEditable)
      },
      apply(tr, oldDecorations, _oldState, newState) {
        const meta = tr.getMeta(mentionPluginKey)
        if (meta?.rebuild) {
          return buildMentionDecorations(newState.doc, isEditable)
        }
        if (!tr.docChanged) {
          return oldDecorations.map(tr.mapping, tr.doc)
        }
        return buildMentionDecorations(newState.doc, isEditable)
      },
    },
    view(view) {
      const sync = () => {
        if (isEditable !== view.editable) {
          isEditable = view.editable
          view.dispatch(
            view.state.tr.setMeta(mentionPluginKey, { rebuild: true }),
          )
        }
      }
      sync()
      return { update: sync }
    },
    props: {
      decorations(state) {
        return mentionPluginKey.getState(state)
      },
      handleClickOn(view, _pos, node, _nodePos, event, direct) {
        if (view.editable || !direct || node.type.name !== 'mention') {
          return false
        }

        const onClick = getOnMentionClick()
        if (!onClick) {
          return false
        }

        event.preventDefault()
        onClick(node.attrs as MentionAttrs)

        return true
      },
      handleDOMEvents: {
        keydown(view, event) {
          if (view.editable) {
            return false
          }

          if (event.key !== 'Enter' && event.key !== ' ') {
            return false
          }

          const onClick = getOnMentionClick()
          const attrs = getMentionAttrsAtDOM(view, event.target)

          if (!onClick || !attrs) {
            return false
          }

          event.preventDefault()
          onClick(attrs)
          return true
        },
      },
    },
  })
}
